import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router';
import { ApiError } from '@awj/api-client';
import { useToast } from '@awj/ui';

import { FormPage } from '~/components/FormPage.tsx';
import { FieldHead } from '~/components/InfoHint.tsx';
import { Select } from '~/components/Select.tsx';
import { useI18n } from '~/i18n/index.tsx';
import { useInviteStaff } from '~/lib/settings-queries.ts';
import { useSession } from '~/lib/session.tsx';

/** Where the staff list lives, tab and all. */
const LIST = '/settings?panel=staff';

/**
 * Inviting somebody into the back office.
 *
 * The invitation creates the account with no password and emails a link — nobody chooses a
 * password for somebody else.
 *
 * SuperAdmin only, and refused at the API as well: this hands out access to payment credentials
 * and to the shop itself.
 */
export function StaffInvitePage() {
  const { t, locale } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const { has } = useSession();

  const invite = useInviteStaff();

  const [draft, setDraft] = useState({ firstName: '', lastName: '', email: '', role: 'Admin' });
  const [error, setError] = useState<string | null>(null);

  const back = () => navigate(LIST);

  if (!has('SuperAdmin')) {
    return <Navigate to="/settings" replace />;
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();

    if (!draft.firstName.trim() || !draft.lastName.trim() || !draft.email.trim()) {
      setError(t('settings.inviteIncomplete'));

      return;
    }

    try {
      await invite.mutateAsync({
        firstName: draft.firstName.trim(),
        lastName: draft.lastName.trim(),
        email: draft.email.trim(),
        role: draft.role,
        // Written in the inviter's language: the colleague is being brought in by them.
        locale,
      });

      toast.show({ title: t('settings.invited'), tone: 'success' });
      back();
    } catch (caught) {
      setError(
        caught instanceof ApiError && caught.is('staff.already_exists')
          ? t('settings.inviteExists')
          : caught instanceof ApiError
            ? (caught.problem.detail ?? t('settings.couldNotInvite'))
            : t('common.somethingWentWrong'),
      );
    }
  }

  return (
    <FormPage
      icon="settings"
      title={t('settings.inviteTitle')}
      subtitle={t('settings.inviteExplain')}
      onBack={back}
      onSubmit={submit}
      submitLabel={t('settings.invite')}
      submitting={invite.isPending}
    >
      <div className="category-form">
        <div className="category-form__row">
          <label className="field">
            <span>{t('settings.firstName')}</span>
            <input
              className="awj-input"
              value={draft.firstName}
              onChange={(event) => setDraft((current) => ({ ...current, firstName: event.target.value }))}
            />
          </label>

          <label className="field">
            <span>{t('settings.lastName')}</span>
            <input
              className="awj-input"
              value={draft.lastName}
              onChange={(event) => setDraft((current) => ({ ...current, lastName: event.target.value }))}
            />
          </label>
        </div>

        <label className="field">
          <span>{t('auth.email')}</span>
          <input
            className="awj-input"
            type="email"
            dir="ltr"
            value={draft.email}
            onChange={(event) => setDraft((current) => ({ ...current, email: event.target.value }))}
          />
        </label>

        <div className="field">
          <FieldHead htmlFor="staff-role" label={t('settings.role')} hint={t('settings.roleHint')} />
          <Select
            id="staff-role"
            value={draft.role}
            onChange={(role) => setDraft((current) => ({ ...current, role }))}
            options={[
              { value: 'Admin', label: t('role.Admin') },
              { value: 'SuperAdmin', label: t('role.SuperAdmin') },
            ]}
          />
        </div>

        {error ? (
          <p className="bilingual__error" role="alert">
            {error}
          </p>
        ) : null}
      </div>
    </FormPage>
  );
}
